import type {EnterDialogModeFunction} from '../conversation-types'
import type React from 'react'
import {useCallback} from 'react'
import type {MarkersStatusAction} from '../hooks/use-markers-dialog-status'
import {useDialogModeActions} from '../stores/selectors'

/**
 * Enters dialog mode for inline comment markers in a diff cell.
 *
 * This hook returns a function that enables the inline comment dialog mode, which converts the cell's role
 * from "gridcell" to "dialog" and manages focus within the markers. It's used when users
 * want to interact with comment threads or start new conversations on diff lines.
 *
 * @param cellRef - Reference to the table cell element that will be converted to dialog mode
 * @param dispatchMarkersStatus - Function to dispatch markers status actions
 * @returns A function that can be called to enter dialog mode with optional configuration
 *
 * The returned function accepts:
 * @param opts - Configuration options for entering dialog mode
 * @param opts.shouldFocusFirstMarker - Whether to automatically focus the first marker
 *   element after entering dialog mode. Defaults to `true`. Set to `false` when you want
 *   to manage focus manually or when navigating directly to a specific thread.
 * @param threadId - Optional thread ID to focus on when entering dialog mode. When provided,
 *   focuses the marker with the matching `data-marker-id` attribute instead of the first marker.
 *
 * @remarks
 * - Only works on cells with the 'diff-text-cell' CSS class
 * - Updates the markers status to 'USER_EXPANDED_MARKERS'
 * - Uses setTimeout to ensure focus is applied after DOM updates
 * - When `shouldFocusFirstMarker` is true, focuses elements with `[data-first-marker="true"]` selector
 * - When `threadId` is provided, focuses elements with `[data-marker-id="${threadId}"]` selector
 */
export function useEnterDialogMode(
  gridCellId: string,
  cellRef: React.RefObject<HTMLTableCellElement>,
  dispatchMarkersStatus: React.Dispatch<MarkersStatusAction>,
): EnterDialogModeFunction {
  const {enableDialogMode} = useDialogModeActions()

  return useCallback(
    (opts: {shouldFocusFirstMarker?: boolean} = {shouldFocusFirstMarker: true}, threadId?: string) => {
      if (cellRef.current?.classList.contains('diff-text-cell')) {
        enableDialogMode(gridCellId)
        dispatchMarkersStatus('USER_EXPANDED_MARKERS')
        // Focus the first marker after the cell has been converted to role="dialog"
        if (opts.shouldFocusFirstMarker) {
          setTimeout(() => {
            ;(cellRef.current?.querySelector('[data-first-marker="true"]') as HTMLElement)?.focus()
          }, 0)
        } else if (threadId) {
          setTimeout(() => {
            /**
             * Note: This behavior is slightly buggy, brittle, and unintuitive.
             *
             * The code as written puts the focus ring and focus on the entire "marker".
             *
             * On initial page load with a review comment hash, e.g. #r1234, this results in the marker container,
             * rather than the referenced comment, being focused. We actually want the correct comment to be focused,
             * but changing this behavior breaks the "navigating from side panel" use case.
             *
             * When navigating from the comment sidepanel to the review comment in the diff, the side
             * effects of this result in the correct comment receiving focus and the focus ring.
             */
            ;(cellRef.current?.querySelector(`[data-marker-id="${threadId}"]`) as HTMLElement)?.focus()
          }, 0)
        }
      }
    },
    [cellRef, enableDialogMode, gridCellId, dispatchMarkersStatus],
  )
}
